
/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';

import {
	PanelBody,
	ToggleControl,
	RangeControl,
} from '@wordpress/components';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

const NETWORKS = [
	{
		key: 'facebook',
		label: 'Facebook',
		buttonLabel: __( 'Share this', 'machete-share-block' ),
		icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>',
	},
	{
		key: 'x',
		label: 'X',
		buttonLabel: __( 'Post this', 'machete-share-block' ),
		icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
	},
	{
		key: 'linkedin',
		label: 'LinkedIn',
		buttonLabel: __( 'Share this', 'machete-share-block' ),
		icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
	},
	{
		key: 'whatsapp',
		label: 'WhatsApp',
		buttonLabel: __( 'Send this', 'machete-share-block' ),
		icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>',
	},
	{
		key: 'pinterest',
		label: 'Pinterest',
		buttonLabel: __( 'Pin this', 'machete-share-block' ),
		icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>',
	},
];

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit( { attributes, setAttributes } ) {
	const { facebook, x, linkedin, whatsapp, pinterest, iconSize, spacing, buttonPadding } = attributes;

	const networkState = { facebook, x, linkedin, whatsapp, pinterest };

	const activeNetworks = NETWORKS.filter( ( n ) => networkState[ n.key ] );

	const blockProps = useBlockProps( {
		className: 'wp-machete-share-block',
	} );

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Social Networks', 'machete-share-block' ) }
					initialOpen={ true }
				>
					{ NETWORKS.map( ( network ) => (
						<ToggleControl
							key={ network.key }
							__nextHasNoMarginBottom
							label={ network.label }
							checked={ !! networkState[ network.key ] }
							onChange={ ( value ) =>
								setAttributes( { [ network.key ]: value } )
							}
						/>
					) ) }
				</PanelBody>
				<PanelBody
					title={ __( 'Appearance', 'machete-share-block' ) }
					initialOpen={ true }
				>
					<RangeControl
						__nextHasNoMarginBottom
						label={ __( 'Icon size (px)', 'machete-share-block' ) }
						value={ iconSize }
						onChange={ ( value ) =>
							setAttributes( { iconSize: value } )
						}
						min={ 16 }
						max={ 64 }
					/>
					<RangeControl
						__nextHasNoMarginBottom
						label={ __( 'Spacing (px)', 'machete-share-block' ) }
						value={ spacing }
						onChange={ ( value ) =>
							setAttributes( { spacing: value } )
						}
						min={ 0 }
						max={ 40 }
					/>
					<RangeControl
						__nextHasNoMarginBottom
						label={ __( 'Button padding (px)', 'machete-share-block' ) }
						value={ buttonPadding }
						onChange={ ( value ) =>
							setAttributes( { buttonPadding: value } )
						}
						min={ 0 }
						max={ 40 }
					/>
				</PanelBody>
			</InspectorControls>
			<div { ...blockProps }>
				{ activeNetworks.length === 0 && (
					<p className="machete-share-block__empty">
						{ __(
							'Enable at least one social network in the block settings.',
							'machete-share-block'
						) }
					</p>
				) }
				{ activeNetworks.length > 0 && (
					<ul
						className="machete-share-block__list"
						style={ { gap: spacing + 'px' } }
					>
						{ activeNetworks.map( ( network ) => (
							<li key={ network.key } className="machete-share-block__item">
								<span
									className={ `machete-share-block__button machete-share-block__button--${ network.key }` }
									aria-label={ network.label }
									style={ { padding: buttonPadding + 'px' } }
								>
									<span
										className="machete-share-block__icon"
										style={ {
											width: iconSize + 'px',
											height: iconSize + 'px',
										} }
										dangerouslySetInnerHTML={ {
											__html: network.icon,
										} }
									/>
									<span className="machete-share-block__label">
										{ network.buttonLabel }
									</span>
								</span>
							</li>
						) ) }
					</ul>
				) }
			</div>
		</>
	);
}
